import * as Activity            from '../activity';
import CalendarTime             from '../components/calendar_time';
import classnames               from 'classnames';
import Conditional              from '../components/conditional';
import getFirstActivityText     from '../contacts/get_first_activity_text';
import getShortName             from '../util/get_short_name';
import isContactBlocked         from '../contacts/is_customer_blocked';
import LeadBadge                from './lead_badge';
import NpsBadge                 from './nps_badge';
import RatingBadge              from './rating_badge';
import React                    from 'react';
import ThumbBadge               from './thumb_badge';

import { ChevronRight }         from 'react-feather';
import { Eye as FollowingIcon } from 'react-feather';
import { User as UserIcon }     from 'react-feather';


export default function ConversationItem({ businessID, conversationID, currentUserID, isNPS, conversation, style }) {
  const href        = `/${businessID}/inbox/${conversation.id}`;
  const unreadCount = conversation?.unreadCount ?? 0;
  const unread      = unreadCount > 0;
  const active      = conversation.id === conversationID;
  const className   = classnames('conversation-item', { unread, active });
  return (
    <a href={href} className={className} style={style}>
      <TopRow {...{ conversation, isNPS }}/>
      <MiddleRow {...{ conversation }}/>
      <BottomRow {...{ currentUserID, conversation }}/>
    </a>
  );
}


// Top Row - customer name and status

function TopRow({ conversation, isNPS }) {
  const [ customer ]  = conversation.customers;
  const isLead        = customerIsLead(customer);
  const hasNpsScore   = isNPS && customerHasNpsScore(customer);
  const reviews       = customer.reviews || {};
  const hasThumbsUp   = !isNPS && customer.promoter === true;
  const hasThumbsDown = !isNPS && customer.promoter === false;

  const { facebook, google, nextdoor, tripAdvisor, yelp } = reviews;

  return (
    <div className="top row">
      <div className="customer-name">
        {customer.name.full}
      </div>
      <div className="customer-status">
        {isLead &&        <LeadBadge/>}
        {hasNpsScore &&   <NpsBadge score={customer.npsScore}/>}
        {facebook &&      <RatingBadge platform="facebook" rating={facebook.rating}/>}
        {google &&        <RatingBadge platform="google" rating={google.rating}/>}
        {nextdoor &&      <RatingBadge platform="nextdoor" rating={nextdoor.rating}/>}
        {tripAdvisor &&   <RatingBadge platform="tripAdvisor" rating={tripAdvisor.rating}/>}
        {yelp &&          <RatingBadge platform="yelp" rating={yelp.rating}/>}
        {hasThumbsUp &&   <ThumbBadge type="up"/>}
        {hasThumbsDown && <ThumbBadge type="down"/>}
      </div>
    </div>
  );
}

export function customerIsLead(customer) {
  return customer.status === 'lead';
}

export function customerHasNpsScore(customer) {
  return typeof customer.npsScore === 'number';
}


// Middle row - description of latest activity

function MiddleRow({ conversation }) {
  return (
    <div className="middle row">
      <LastActivity {...{ conversation }}/>
    </div>
  );
}

function LastActivity({ conversation }) {
  const text = getLastActivityText(conversation);
  return (
    <div className="last-activity">{text || <>&nbsp;</>}</div>
  );
}


// Bottom row - timestamp, following icon, assignee, right arrow

function BottomRow({ currentUserID, conversation }) {
  return (
    <div className="bottom row">
      <Timestamp {...{ conversation }}/>
      <div className="conversation-meta">
        <Following {...{ conversation, currentUserID }}/>
        <Assignee {...{ conversation, currentUserID }}/>
        <ChevronRight className="right-arrow"/>
      </div>
    </div>
  );
}

function Timestamp({ conversation }) {
  const { lastActive } = conversation;
  return (
    <div className="timestamp">
      <CalendarTime time={lastActive}/>
    </div>
  );
}

export function Following({ conversation, currentUserID }) {
  const participants = conversation?.participants ?? [];
  const isFollowing  = participants.some(participant => participant.user.id === currentUserID);
  return (
    <Conditional condition={isFollowing}>
      <FollowingIcon className="following-icon"/>
    </Conditional>
  );
}

export function Assignee({ conversation, currentUserID }) {
  const name = getAssigneeName({ conversation, currentUserID });
  return (
    <Conditional condition={name}>
      <div className="assignee">
        <UserIcon className="user-icon"/>
        <div className="label">{name}</div>
      </div>
    </Conditional>
  );
}

export function getAssigneeName({ conversation, currentUserID }) {
  const [ assignee ] = conversation.assignees;
  if (assignee?.user.id === currentUserID)
    return 'You';
  else
    return getShortName(assignee?.user.name);
}


function getLastActivityText(conversation) {
  const [ customer ] = conversation.customers;
  if (isContactBlocked(conversation))
    return 'Contact has been blocked';
  else if (isContactAdded(conversation))
    return getFirstActivityText({ customer });
  else {
    const isRepeatContact = (conversation.lastActivity.type === 'Transaction');
    if (isRepeatContact)
      return Activity.getTransactionText({ activity: conversation.lastActivity });
    else
      return Activity.getText({ activity: conversation.lastActivity });
  }
}


function isContactAdded(conversation) {
  const { lastActivity } = conversation;
  const [ customer ]     = conversation.customers;

  const hasLastActivity = !!lastActivity;

  const lastActivityIsFirstTransaction = hasLastActivity &&
    lastActivity.type === 'Transaction' &&
    customer.transactionCount === 1;

  return !hasLastActivity || lastActivityIsFirstTransaction;
}
